import * as React from "react";
import { useCallback } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import { IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SbuxCollapsiblePanel, {
  Panel,
  PanelItem,
} from "../SbuxCollapsiblePanel";
import type { ReactNode } from "react";
import drawerStyles from "./styles";
import useCss from "../../hooks/useCss";

export type { Panel, PanelItem };

const drawerWidth = 250;

interface OpenProps {
  open?: boolean;
}

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})<OpenProps>(({ theme, open }) => ({
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  marginLeft: "40px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  height: 35,
  justifyContent: "flex-end",
}));

interface Props {
  panelData: Panel[];
  children?: ReactNode;
}

const SbuxDrawer = ({ panelData, children }: Props) => {
  const theme = useTheme();
  const classes = useCss(drawerStyles);
  const [open, setOpen] = React.useState(true);
  const handleDrawerOpen = useCallback(() => setOpen(true), []);
  const handleDrawerClose = useCallback(() => setOpen(false), []);

  return (
    <Box className={classes.drawerRoot}>
      {/* Collapsed hamburger strip - shown when drawer is closed */}
      {!open && (
        <Box className={classes.hamburgerStrip}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            className={classes.hamburgerButton}
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>
      )}

      <Drawer
        sx={{
          zIndex: 2,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            position: "absolute",
            width: drawerWidth,
            boxSizing: "border-box",
            height: "100%",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton
            onClick={handleDrawerClose}
            className={classes.closeButton}
          >
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon sx={{ fontSize: 28 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 28 }} />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          <SbuxCollapsiblePanel panelData={panelData} />
        </List>
      </Drawer>

      <Main open={open}>{children}</Main>
    </Box>
  );
};
export default SbuxDrawer;
